import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class NavigationHistoryController extends Controller {
  static values = { fallback: { type: String, default: "/" } };

  connect() {
    this.record = this.record.bind(this);
    document.addEventListener("turbo:load", this.record);
    this.record();
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.record);
  }

  back(event) {
    event?.preventDefault();

    if (globalThis.history.length > 1) {
      // Let Turbo handle popstate as a restoration visit, preserving its
      // cached page and scroll position instead of replacing the history entry.
      globalThis.history.back();
      return;
    }

    this.visit(this.fallbackUrl(event));
  }

  record() {
    const currentUrl = this.internalUrl(globalThis.location.href);
    if (!currentUrl) return;

    const history = this.history;
    if (history.at(-1) === currentUrl) return;

    this.history = [...history, currentUrl].slice(-100);
  }

  fallbackUrl(event) {
    const requestedFallback = event?.currentTarget?.getAttribute("href") || this.fallbackValue;
    return this.internalUrl(requestedFallback) || new URL("/", globalThis.location.origin).href;
  }

  internalUrl(value) {
    if (!value) return null;

    try {
      const url = new URL(value, globalThis.location.origin);
      return url.origin === globalThis.location.origin ? url.href : null;
    } catch {
      return null;
    }
  }

  visit(url) {
    Turbo.visit(url, { action: "replace" });
  }

  get history() {
    try {
      const history = JSON.parse(globalThis.sessionStorage.getItem(this.storageKey) || "[]");
      return Array.isArray(history) ? history.filter((url) => this.internalUrl(url)) : [];
    } catch {
      return [];
    }
  }

  set history(history) {
    try {
      globalThis.sessionStorage.setItem(this.storageKey, JSON.stringify(history));
    } catch {
      // Navigation still works with the internal fallback when storage is unavailable.
    }
  }

  get storageKey() {
    return "itoons.navigationHistory";
  }
}
